import { zodResolver } from '@hookform/resolvers/zod';
import type { DeviceModel } from '@packages/shared-types';
import { MoreHorizontal, Pencil, Plus } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import toast from 'react-hot-toast';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { LoadingLogo } from '@/components/shared/LoadingLogo';
import { PageHeader } from '@/components/shared/PageHeader';
import { TableCard } from '@/components/shared/TableCard';
import {
  listDeviceModels,
  createDeviceModel,
  type CreateDeviceModelPayload,
} from '@/services/deviceModelService';

const DEVICE_FEATURES = [
  { key: 'rfid', labelKey: 'pages.deviceModels.form.rfid' },
  { key: 'barcode', labelKey: 'pages.deviceModels.form.barcode' },
  { key: 'radar', labelKey: 'pages.deviceModels.form.radar' },
  { key: 'battery', labelKey: 'pages.deviceModels.form.battery' },
  { key: 'temperature', labelKey: 'pages.deviceModels.form.temperature' },
  { key: 'powerLevel', labelKey: 'pages.deviceModels.form.powerLevel' },
  { key: 'antennaStatus', labelKey: 'pages.deviceModels.form.antennaStatus' },
  { key: 'connectionStatus', labelKey: 'pages.deviceModels.form.connectionStatus' },
] as const;

const FEATURE_KEY_TO_LABEL: Record<string, string> = Object.fromEntries(
  DEVICE_FEATURES.map(({ key, labelKey }) => [key, labelKey]),
);

function camelCaseToTitleCase(str: string): string {
  return str
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (s) => s.toUpperCase())
    .trim();
}

const deviceModelFormSchema = z.object({
  name: z.string().min(1, 'pages.deviceModels.form.nameRequired'),
  manufacturer: z.string().optional(),
  slug: z
    .string()
    .min(1, 'pages.deviceModels.form.slugRequired')
    .regex(/^[a-z0-9-]+$/, 'pages.deviceModels.form.slugHelper'),
  rfid: z.boolean().optional(),
  barcode: z.boolean().optional(),
  radar: z.boolean().optional(),
  battery: z.boolean().optional(),
  temperature: z.boolean().optional(),
  powerLevel: z.boolean().optional(),
  antennaStatus: z.boolean().optional(),
  connectionStatus: z.boolean().optional(),
});

type DeviceModelFormValues = z.infer<typeof deviceModelFormSchema>;

function getFeaturesFromForm(values: DeviceModelFormValues): Record<string, boolean> {
  const features: Record<string, boolean> = {};
  for (const { key } of DEVICE_FEATURES) {
    const val = values[key as keyof DeviceModelFormValues];
    features[key] = val === true;
  }
  return features;
}

export function DeviceModelsPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const title = t('pages.deviceModels.title');
  const description = t('pages.deviceModels.description');
  const createLabel = t('pages.deviceModels.actions.create');
  const emptyLabel = t('pages.deviceModels.list.empty');
  const loadingLabel = t('pages.deviceModels.list.loading');
  const successCreate = t('pages.deviceModels.success.create');
  const errorLoad = t('pages.deviceModels.error.load');
  const errorCreate = t('pages.deviceModels.error.create');
  const featuresTableLabel = t('pages.deviceModels.table.features');

  const [deviceModels, setDeviceModels] = useState<DeviceModel[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);

  const {
    register: registerCreate,
    handleSubmit: handleSubmitCreate,
    control: controlCreate,
    formState: { errors: createErrors, isSubmitting: isCreating },
    reset: resetCreate,
  } = useForm<DeviceModelFormValues>({
    resolver: zodResolver(deviceModelFormSchema),
    defaultValues: {
      name: '',
      manufacturer: '',
      slug: '',
      rfid: false,
      barcode: false,
      radar: false,
      battery: false,
      temperature: false,
      powerLevel: false,
      antennaStatus: false,
      connectionStatus: false,
    },
  });

  const loadDeviceModels = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await listDeviceModels();
      setDeviceModels(data);
    } catch (err) {
      const errorMsg =
        err instanceof Error ? err.message : errorLoad;
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  }, [errorLoad]);

  useEffect(() => {
    void loadDeviceModels();
  }, [loadDeviceModels]);

  const handleCreate = async (values: DeviceModelFormValues): Promise<void> => {
    try {
      const payload: CreateDeviceModelPayload = {
        name: values.name,
        manufacturer: values.manufacturer || undefined,
        slug: values.slug,
        features: getFeaturesFromForm(values),
      };
      const newModel = await createDeviceModel(payload);
      setIsCreateDialogOpen(false);
      resetCreate();
      toast.success(successCreate);
      navigate(`/device-models/${newModel.id}/edit`);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : errorCreate;
      setError(errorMessage);
      toast.error(errorMessage);
    }
  };

  const formatDate = (date: Date | string): string => {
    return new Date(date).toLocaleDateString();
  };

  const formatFeatures = (features: Record<string, boolean>): string[] => {
    return Object.entries(features)
      .filter(([, enabled]) => enabled)
      .map(([key]) => key);
  };

  const createDialogTitle = t('pages.deviceModels.dialogs.create.title');
  const createDialogDescription = t('pages.deviceModels.dialogs.create.description');

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title={title}
        description={description}
        actions={
          <Button onClick={() => setIsCreateDialogOpen(true)}>
            <Plus className="h-4 w-4" />
            {createLabel}
          </Button>
        }
      />

      {error && (
        <Card className="border-destructive bg-destructive/10">
          <CardContent className="p-4">
            <p className="text-sm text-destructive">{error}</p>
          </CardContent>
        </Card>
      )}

      <TableCard>
        {isLoading ? (
          <div className="flex flex-col items-center gap-3 py-8">
            <LoadingLogo size="sm" />
            <span className="text-sm text-muted-foreground">{loadingLabel}</span>
          </div>
        ) : deviceModels.length === 0 ? (
          <div className="py-8 text-center text-sm text-muted-foreground">
            {emptyLabel}
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('pages.deviceModels.table.name')}</TableHead>
                <TableHead>{t('pages.deviceModels.table.manufacturer')}</TableHead>
                <TableHead>{t('pages.deviceModels.table.slug')}</TableHead>
                <TableHead>{featuresTableLabel}</TableHead>
                <TableHead>{t('pages.deviceModels.table.created')}</TableHead>
                <TableHead className="w-[70px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {deviceModels.map((model) => (
                <TableRow
                  key={model.id}
                  onRowClick={() => navigate(`/device-models/${model.id}/edit`)}
                >
                  <TableCell className="font-medium">{model.name}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {model.manufacturer || '—'}
                  </TableCell>
                  <TableCell className="font-mono text-sm">
                    {model.slug}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {formatFeatures(model.features || {}).map((key) => (
                        <Badge key={key} variant="secondary" className="text-xs">
                          {FEATURE_KEY_TO_LABEL[key]
                            ? t(FEATURE_KEY_TO_LABEL[key])
                            : camelCaseToTitleCase(key)}
                        </Badge>
                      ))}
                      {formatFeatures(model.features || {}).length === 0 && (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatDate(model.createdAt)}
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/device-models/${model.id}/edit`);
                          }}
                        >
                          <Pencil className="mr-2 h-4 w-4" />
                          {t('pages.deviceModels.actions.edit')}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </TableCard>

      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{createDialogTitle}</DialogTitle>
            <DialogDescription>{createDialogDescription}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmitCreate(handleCreate)}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="create-name">
                  {t('pages.deviceModels.form.name')} *
                </Label>
                <Input
                  id="create-name"
                  {...registerCreate('name')}
                  placeholder={t('pages.deviceModels.form.name')}
                />
                {createErrors.name && (
                  <p className="text-sm text-destructive">
                    {t(createErrors.name.message || 'pages.deviceModels.form.nameRequired')}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="create-manufacturer">
                  {t('pages.deviceModels.form.manufacturer')}
                </Label>
                <Input
                  id="create-manufacturer"
                  {...registerCreate('manufacturer')}
                  placeholder={t('pages.deviceModels.form.manufacturer')}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="create-slug">
                  {t('pages.deviceModels.form.slug')} *
                </Label>
                <Input
                  id="create-slug"
                  {...registerCreate('slug')}
                  placeholder="chainway-c5"
                />
                {createErrors.slug && (
                  <p className="text-sm text-destructive">
                    {t(createErrors.slug.message || 'pages.deviceModels.form.slugRequired')}
                  </p>
                )}
                <p className="text-xs text-muted-foreground">
                  {t('pages.deviceModels.form.slugHelper')}
                </p>
              </div>
              <div className="space-y-2">
                <Label>{t('pages.deviceModels.form.features')}</Label>
                <div className="grid grid-cols-2 gap-2 pt-2">
                  {DEVICE_FEATURES.map(({ key, labelKey }) => (
                    <Controller
                      key={key}
                      name={key}
                      control={controlCreate}
                      render={({ field }) => (
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id={`create-${key}`}
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                          <Label
                            htmlFor={`create-${key}`}
                            className="text-sm font-normal cursor-pointer"
                          >
                            {t(labelKey)}
                          </Label>
                        </div>
                      )}
                    />
                  ))}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setIsCreateDialogOpen(false);
                  resetCreate();
                }}
              >
                {t('pages.deviceModels.actions.cancel')}
              </Button>
              <Button type="submit" disabled={isCreating}>
                {isCreating ? t('pages.deviceModels.actions.saving') : createLabel}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
